import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { analyzeDomain, whyThisName, type DomainAnalysis } from '@/lib/domainInsights';
import { fireEvent } from '@/lib/analytics';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useParallaxRef } from '@/hooks/useParallax';
import { motion } from 'framer-motion';

interface DomainInsightsProps {
  domain: string;
  binPrice?: number;
  tld?: string;
  tags?: string[];
  variant?: 'light' | 'dark';
  showHeading?: boolean;
}

const ScoreMeter: React.FC<{ score: number; variant: 'light' | 'dark' }> = ({
  score,
  variant,
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 85) return 'bg-emerald-500';
    if (score >= 70) return 'bg-teal-500';
    return 'bg-blue-500';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 85) return 'Standout potential';
    if (score >= 70) return 'Strong brand fit';
    return 'Solid foundation';
  };

  const getScoreBand = (score: number) => {
    if (score >= 85) return 'standout';
    if (score >= 70) return 'strong';
    return 'solid';
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span
          className={`text-sm font-medium ${
            variant === 'dark' ? 'text-slate-300' : 'text-muted-foreground'
          }`}
        >
          Domain Score
        </span>
        <span
          className={`text-sm font-semibold ${
            variant === 'dark' ? 'text-white' : 'text-foreground'
          }`}
        >
          {score}/100 · {getScoreLabel(score)}
        </span>
      </div>
      <div
        className={`w-full rounded-full h-2 relative ${
          variant === 'dark' ? 'bg-white/10' : 'bg-muted'
        }`}
      >
        <div
          className={`h-2 rounded-full transition-all duration-700 ${getScoreColor(score)}`}
          style={{ width: `${Math.max(0, Math.min(100, (score - 50) * 2))}%` }}
          role="progressbar"
          aria-valuenow={score}
          aria-valuemin={50}
          aria-valuemax={100}
          aria-label={`Domain score: ${score} out of 100, ${getScoreLabel(score)}`}
        />
      </div>
      <div
        className={`flex justify-between text-xs ${
          variant === 'dark' ? 'text-slate-400' : 'text-muted-foreground'
        }`}
      >
        <span>Solid (50-69)</span>
        <span>Strong (70-84)</span>
        <span>Standout (85+)</span>
      </div>
    </div>
  );
};

const AttributeBadge: React.FC<{
  label: string;
  value: string;
  tier?: 'high' | 'med' | 'low' | 'premium' | 'mainstream' | 'niche';
}> = ({ label, value, tier }) => {
  const getBadgeVariant = (tier?: string) => {
    switch (tier) {
      case 'high':
      case 'premium':
        return 'default';
      case 'med':
      case 'mainstream':
        return 'secondary';
      case 'low':
      case 'niche':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <Badge variant={getBadgeVariant(tier)} className="text-xs font-medium">
      {label}: {value}
    </Badge>
  );
};

export default function DomainInsights({
  domain,
  binPrice,
  tld,
  tags,
  variant = 'light',
  showHeading = true,
}: DomainInsightsProps) {
  const [analysis, setAnalysis] = useState<DomainAnalysis | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const titleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 60 });

  useEffect(() => {
    const domainAnalysis = analyzeDomain(domain, { bin: binPrice, tld, tags });
    setAnalysis(domainAnalysis);

    // Track analytics on first render
    if (!hasTrackedView) {
      fireEvent('insights_view', {
        domain,
        score: domainAnalysis.score,
        tldTier: domainAnalysis.tldTier,
        length: domainAnalysis.length,
      });
      setHasTrackedView(true);
    }
  }, [domain, binPrice, tld, tags, hasTrackedView]);

  const handleDetailsToggle = () => {
    if (!showDetails) {
      fireEvent('insights_more_open', { domain, score: analysis?.score });
    }
    setShowDetails(!showDetails);
  };

  const whyText = useMemo(
    () => (analysis ? whyThisName({ name: domain, analysis }) : ''),
    [analysis, domain],
  );

  const statChips = useMemo(() => {
    if (!analysis) return [];
    const chips: { label: string; value: string }[] = [];
    chips.push({ label: 'Length', value: `${analysis.length} chars` });
    chips.push({ label: 'Words', value: `${analysis.words}` });
    chips.push({ label: 'TLD', value: analysis.tld });
    if (analysis.nicheHints.length > 0) {
      chips.push({ label: 'Fit', value: analysis.nicheHints[0] });
    }
    return chips;
  }, [analysis]);

  const valueBullets = useMemo(() => {
    if (!analysis) return [];
    const bullets: string[] = [];
    bullets.push('Brandability: short, clear, and easy to say—passes the radio test.');
    bullets.push(
      analysis.keywordStrength === 'high'
        ? 'SEO & Demand: aligns with high-intent keywords for the niche.'
        : 'SEO & Demand: memorable name that supports paid and organic quality scores.',
    );
    bullets.push(
      analysis.nicheHints.length > 0
        ? `Category Fit: instantly signals ${analysis.nicheHints[0].toLowerCase()} buyers.`
        : 'Category Fit: clearly signals the service/industry you lead.',
    );
    bullets.push('Equity: one-of-one digital real estate with long-term resale potential.');
    return bullets;
  }, [analysis]);

  if (!analysis) return null;

  const cardClasses =
    variant === 'dark'
      ? 'bg-white/5 border-white/10 text-white shadow-[0_10px_40px_rgba(0,0,0,0.35)]'
      : 'bg-card text-foreground';

  const subtitleClasses =
    variant === 'dark' ? 'text-slate-300' : 'text-muted-foreground';

  return (
    <>
      {showHeading && (
        <div className="text-center mb-8">
          <h2
            ref={titleRef as React.RefObject<HTMLHeadingElement>}
            className={`text-3xl md:text-4xl font-bold ${variant === 'dark' ? 'text-white' : 'text-foreground'}`}
          >
            Valuation & Strategic Insights
          </h2>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <Card className={`mt-2 rounded-2xl backdrop-blur ${cardClasses}`}>
          <CardContent className="space-y-6 pt-6">
            {/* Score Meter */}
            <ScoreMeter score={analysis.score} variant={variant} />

            {/* Attribute Badges / Stats */}
            <div className="flex flex-wrap gap-2">
              <AttributeBadge
                label="Length"
                value={`${analysis.length} chars`}
                tier={analysis.length <= 8 ? 'high' : analysis.length <= 12 ? 'med' : 'low'}
              />
              <AttributeBadge label="TLD" value={analysis.tldLabel} tier={analysis.tldTier} />
              <AttributeBadge
                label="Brandability"
                value={analysis.brandability}
                tier={analysis.brandability}
              />
              <AttributeBadge label="Clarity" value={analysis.clarity} tier={analysis.clarity} />
              <AttributeBadge
                label="Keywords"
                value={analysis.keywordStrength}
                tier={analysis.keywordStrength}
              />
              <AttributeBadge
                label="Memorability"
                value={analysis.memorability}
                tier={analysis.memorability}
              />
              <AttributeBadge
                label="Versatility"
                value={analysis.versatility}
                tier={analysis.versatility}
              />
              {analysis.nicheHints.length > 0 && (
                <AttributeBadge label="Perfect for" value={analysis.nicheHints[0]} />
              )}
            </div>

            {/* Micro stats strip */}
            {statChips.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs">
                {statChips.map((chip, idx) => (
                  <span
                    key={idx}
                    className={`px-3 py-1 rounded-full border ${
                      variant === 'dark'
                        ? 'border-white/15 text-slate-200 bg-white/5'
                        : 'border-border text-foreground bg-muted/60'
                    }`}
                  >
                    <span className="font-semibold">{chip.label}:</span> {chip.value}
                  </span>
                ))}
              </div>
            )}

            {/* Why This Name Works */}
            <div className="space-y-3">
              <h3
                className={`font-semibold ${
                  variant === 'dark' ? 'text-white' : 'text-foreground'
                }`}
              >
                Why this name works
              </h3>
              <p className={`${subtitleClasses} leading-relaxed`}>{whyText}</p>
              <ul className="space-y-2">
                {valueBullets.map((bullet, idx) => (
                  <li
                    key={idx}
                    className={`flex items-start gap-2 text-sm ${
                      variant === 'dark' ? 'text-slate-200' : 'text-foreground/80'
                    }`}
                  >
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/80" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* More Details Toggle */}
            {analysis.quickReasons.length > 0 && (
              <div className="space-y-3">
                <button
                  onClick={handleDetailsToggle}
                  className={`flex items-center gap-2 text-sm font-medium ${
                    variant === 'dark'
                      ? 'text-primary hover:text-primary/80'
                      : 'text-primary hover:text-primary/80'
                  } transition-colors`}
                  aria-expanded={showDetails}
                  aria-controls="domain-insights-details"
                >
                  {showDetails ? 'Hide details' : 'More details'}
                  {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {showDetails && (
                  <div
                    id="domain-insights-details"
                    className={`space-y-2 pl-4 border-l-2 ${
                      variant === 'dark' ? 'border-white/10' : 'border-muted'
                    } animate-in slide-in-from-top-2 duration-200`}
                  >
                    <h4
                      className={`text-sm font-medium ${
                        variant === 'dark' ? 'text-white' : 'text-foreground'
                      }`}
                    >
                      Key strengths:
                    </h4>
                    <ul className="space-y-1">
                      {analysis.quickReasons.map((reason, index) => (
                        <li
                          key={index}
                          className={`text-sm flex items-start gap-2 ${
                            variant === 'dark' ? 'text-slate-200' : 'text-muted-foreground'
                          }`}
                        >
                          <span className="text-primary mt-1">•</span>
                          {reason}
                        </li>
                      ))}
                    </ul>

                    {analysis.words > 1 && (
                      <div
                        className={`pt-2 text-xs ${
                          variant === 'dark' ? 'text-slate-300' : 'text-muted-foreground'
                        }`}
                      >
                        <span className="font-medium">Structure:</span> {analysis.words} word
                        {analysis.words > 1 ? 's' : ''}
                        {analysis.hasHyphen && ', hyphenated'}
                        {analysis.hasNumber && ', includes numbers'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </>
  );
}
